import React, { useEffect, useMemo, useState } from 'react';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ApartmentIcon from '@mui/icons-material/Apartment';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import VerifiedIcon from '@mui/icons-material/Verified';
import BlockIcon from '@mui/icons-material/Block';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {
  Box,
  Tooltip,
  IconButton,
  InputAdornment,
  Grid,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Stack,
  Button
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import { getPartnerTypesData } from '../../store/Slice/Global/GlobalSlice';
import { pillTabButtonSx } from '../Global/Styling';

const TAB_ICONS = {
  'all partners': <GroupsIcon />,
  company: <ApartmentIcon />,
  individual: <PersonIcon />,
  activated: <CheckCircleIcon />,
  incomplete: <PendingActionsIcon />,
  approved: <VerifiedIcon />,
  inactive: <BlockIcon />
};

export default function OnBoardingToolbar({
  parent = 'onboarding',
  pageMenu,
  filterName,
  setFilterName,
  filterState,
  setFilterState,
  statusOptions = [],
  activeTab = 'all',
  setActiveTab,
  isPartnerTypeModalOpen,
  setIsPartnerTypeModalOpen
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isOpenFilter, openFilter] = useState(false);
  const { CityDrop, PartnerStatusDrop } = useGlobalSelector();

  const pageData = useMemo(
    () =>
      pageMenu?.reduce((acc, item) => {
        acc[item?.subPageCode] = item?.isEnable;
        return acc;
      }, {}),
    [pageMenu]
  );

  useEffect(() => {
    if (!PartnerStatusDrop || PartnerStatusDrop.length === 0) {
      dispatch(getPartnerTypesData());
    }
  }, [PartnerStatusDrop, dispatch]);

  const tabs = [
    { key: 'all', label: 'All Partners', typeId: null, partnerStatusTypeId: null },
    ...(PartnerStatusDrop || []).map((item) => ({
      key: `type-${item?.value}`,
      label: item?.label,
      typeId: item?.value,
      partnerStatusTypeId: null
    })),
    ...(statusOptions || []).map((item) => ({
      key: `status-${item?.value}`,
      label: item?.label,
      typeId: null,
      partnerStatusTypeId: item?.value
    }))
  ];

  const handleTabClick = (tab) => {
    if (setActiveTab) setActiveTab(tab.key);
    setFilterState((prev) => ({
      ...prev,
      typeId: tab.typeId,
      partnerStatusTypeId: tab.partnerStatusTypeId,
      pageNumber: 1
    }));
  };

  const handleRemoveFilter = () => {
    setFilterState((prev) => ({
      ...prev,
      typeId: null,
      partnerStatusTypeId: null,
      city: null,
      cityId: null,
      pageNumber: 1
    }));
    setFilterName('');
    if (setActiveTab) setActiveTab('all');
    openFilter(false);
  };

  const handleSelectCompany = () => {
    if (setIsPartnerTypeModalOpen) setIsPartnerTypeModalOpen(false);
    const companyType = PartnerStatusDrop?.find(
      (item) =>
        item?.label?.toLowerCase().includes('company') ||
        item?.label?.toLowerCase().includes('corporate') ||
        item?.value !== 66
    );
    navigate(`/${parent}/add`, {
      state: { partnerType: 'company', typeId: companyType?.value }
    });
  };

  const handleSelectIndividual = () => {
    if (setIsPartnerTypeModalOpen) setIsPartnerTypeModalOpen(false);
    const individualType = PartnerStatusDrop?.find(
      (item) => item?.value === 66 || item?.label?.toLowerCase().includes('individual')
    );
    navigate(`/${parent}/add`, {
      state: { partnerType: 'individual', typeId: individualType?.value || 66 }
    });
  };

  return (
    <>
      {/* Top Toolbar: Tabs on Left + Search & Filter Toggle on Right */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 1.5,
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        {/* Left Filter Tabs */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            overflowX: 'auto',
            p: '2px 2px 6px',
            '::-webkit-scrollbar': { display: 'none' }
          }}
        >
          {pageData?.SEARCH &&
            tabs.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <Box
                  key={tab.key}
                  component="button"
                  onClick={() => handleTabClick(tab)}
                  sx={{ ...pillTabButtonSx(isActive), px: 2 }}
                >
                  {TAB_ICONS[tab.label?.toLowerCase()]}
                  {tab.label}
                </Box>
              );
            })}
        </Box>

        {/* Right Search Input & Filters Toggle Button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, ml: 'auto' }}>
          {pageData?.SEARCH && (
            <>
              <TextField
                size="small"
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
                placeholder="Search by partner name, ID, mobile..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                    </InputAdornment>
                  )
                }}
                sx={{
                  width: { xs: '100%', sm: 280, md: 310 },
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#fff',
                    borderRadius: '10px',
                    fontSize: '13px',
                    '& fieldset': {
                      borderColor: '#e2e8f0'
                    },
                    '&:hover fieldset': {
                      borderColor: '#cbd5e1'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#0284c7'
                    }
                  }
                }}
              />

              <Button
                variant="outlined"
                onClick={() => openFilter((prev) => !prev)}
                startIcon={<FilterListIcon sx={{ fontSize: 18 }} />}
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '13px',
                  color: isOpenFilter ? '#0284c7' : '#475569',
                  borderColor: isOpenFilter ? '#0284c7' : '#e2e8f0',
                  bgcolor: isOpenFilter ? 'rgba(2, 132, 199, 0.06)' : '#fff',
                  height: 40,
                  px: 2,
                  '&:hover': {
                    borderColor: '#0284c7',
                    bgcolor: 'rgba(2, 132, 199, 0.06)',
                    color: '#0284c7'
                  }
                }}
              >
                Filters
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Expandable Filters Section */}
      {isOpenFilter && (
        <Box
          sx={{
            p: 2.5,
            bgcolor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0'
          }}
        >
          <Grid container spacing={2} alignItems="center">
            {/* Partner Type */}
            <Grid item xs={12} sm={4} md={3.5}>
              <Select
                fullWidth
                size="small"
                value={filterState?.typeId ?? ' '}
                onChange={(e) => {
                  const val = e.target.value === ' ' ? null : e.target.value;
                  setFilterState((prev) => ({ ...prev, typeId: val, pageNumber: 1 }));
                }}
                sx={{
                  bgcolor: '#fff',
                  borderRadius: '8px',
                  fontSize: '13px'
                }}
              >
                <MenuItem value=" " disabled>
                  Select Partner Type
                </MenuItem>
                {PartnerStatusDrop?.length > 0 &&
                  PartnerStatusDrop.map((item) => (
                    <MenuItem value={item?.value} key={item?.value}>
                      {item?.label}
                    </MenuItem>
                  ))}
              </Select>
            </Grid>

            {/* City Autocomplete */}
            <Grid item xs={12} sm={4} md={3.5}>
              <Autocomplete
                id="combo-box-city"
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    placeholder="Select City"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: '#fff',
                        borderRadius: '8px',
                        fontSize: '13px'
                      }
                    }}
                  />
                )}
                value={filterState?.city ?? null}
                options={CityDrop || []}
                getOptionLabel={(option) => option.label || ''}
                renderOption={(props, option) => (
                  <li {...props} key={option.value || option.label}>
                    {option.label}
                  </li>
                )}
                onChange={(e, newValue) => {
                  setFilterState((prev) => ({
                    ...prev,
                    city: newValue ?? null,
                    cityId: newValue ? newValue.value : null,
                    pageNumber: 1
                  }));
                }}
              />
            </Grid>

            {/* Status Select */}
            <Grid item xs={12} sm={3} md={3.5}>
              <Select
                fullWidth
                size="small"
                onChange={(e) => {
                  const val = e.target.value === ' ' ? null : e.target.value;
                  setFilterState((prev) => ({
                    ...prev,
                    partnerStatusTypeId: val,
                    pageNumber: 1
                  }));
                }}
                value={filterState?.partnerStatusTypeId ?? ' '}
                sx={{
                  bgcolor: '#fff',
                  borderRadius: '8px',
                  fontSize: '13px'
                }}
              >
                <MenuItem value=" " disabled>
                  Select Status
                </MenuItem>
                {statusOptions?.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            {/* Reset Filter Button */}
            <Grid item xs={12} sm={1} md="auto">
              <Tooltip title="Reset Filter" arrow>
                <IconButton
                  onClick={handleRemoveFilter}
                  sx={{
                    bgcolor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    color: '#64748b',
                    p: 1,
                    '&:hover': { bgcolor: '#f1f5f9', color: '#1e293b' }
                  }}
                >
                  <FilterListOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Partner Type Selection Popup */}
      <Dialog
        open={Boolean(isPartnerTypeModalOpen)}
        onClose={() => setIsPartnerTypeModalOpen && setIsPartnerTypeModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 1.5,
            boxShadow: '0 20px 40px rgba(15, 23, 42, 0.15)',
            border: '1px solid #e2e8f0'
          }
        }}
      >
        <DialogTitle
          sx={{
            p: 2,
            pb: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#0284c7',
                fontSize: '18px',
                letterSpacing: '-0.2px'
              }}
            >
              Partner Type
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px', mt: 0.25 }}>
              Select the partner category to proceed
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setIsPartnerTypeModalOpen && setIsPartnerTypeModalOpen(false)}
            sx={{
              color: '#94a3b8',
              '&:hover': { bgcolor: '#f1f5f9', color: '#1e293b' }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2, pt: 1.5 }}>
          <Stack spacing={2}>
            {/* Company Button */}
            <Button
              fullWidth
              onClick={handleSelectCompany}
              sx={{
                p: 2,
                borderRadius: '12px',
                border: '1.5px solid #e2e8f0',
                bgcolor: '#ffffff',
                textAlign: 'left',
                justifyContent: 'space-between',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: '#0284c7',
                  bgcolor: '#f0f9ff',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(2, 132, 199, 0.12)'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '10px',
                    bgcolor: '#f3e8ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#9333ea'
                  }}
                >
                  <ApartmentIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '15px',
                      color: '#1e293b',
                      textTransform: 'none'
                    }}
                  >
                    Company
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: '#64748b',
                      textTransform: 'none',
                      mt: 0.2
                    }}
                  >
                    Corporate & business entities
                  </Typography>
                </Box>
              </Box>
              <ArrowForwardIosIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
            </Button>

            {/* Individual Button */}
            <Button
              fullWidth
              onClick={handleSelectIndividual}
              sx={{
                p: 2,
                borderRadius: '12px',
                border: '1.5px solid #e2e8f0',
                bgcolor: '#ffffff',
                textAlign: 'left',
                justifyContent: 'space-between',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: '#0284c7',
                  bgcolor: '#f0f9ff',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(2, 132, 199, 0.12)'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '10px',
                    bgcolor: '#e0f2fe',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#0284c7'
                  }}
                >
                  <PersonIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '15px',
                      color: '#1e293b',
                      textTransform: 'none'
                    }}
                  >
                    Individual
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: '#64748b',
                      textTransform: 'none',
                      mt: 0.2
                    }}
                  >
                    Sole proprietorship & individuals
                  </Typography>
                </Box>
              </Box>
              <ArrowForwardIosIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
